/**
 * TeacherWorkload — Section 6 of the Office Manager Report (weekly variant).
 *
 * Sessions and hours per teacher for the 7-day window.
 * data-testid: candeza-reports-operations-workload-*
 */

import React from 'react';
import styled from 'styled-components';
import type { TeacherWorkloadEntry } from '@/api/reports';

// ── Styled components ─────────────────────────────────────────────────────────
// Prompt 11E.8: no horizontal-scroll wrapper — same fix idiom as
// TodaysSchedule.tsx's TableScroll.

const TableScroll = styled.div`
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
`;

const Table = styled.table`
  width: 100%;
  min-width: 420px;
  border-collapse: collapse;
  font-size: ${({ theme }) => theme.typography.fontSizes.sm};

  th {
    text-align: left;
    padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.sm}`};
    color: ${({ theme }) => theme.colors.textSecondary};
    font-weight: ${({ theme }) => theme.typography.fontWeights.medium};
    border-bottom: 2px solid ${({ theme }) => theme.colors.border};
  }

  td {
    padding: ${({ theme }) => `${theme.spacing.xs} ${theme.spacing.sm}`};
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }

  tr:last-child td {
    border-bottom: none;
  }
`;

const Empty = styled.div`
  color: ${({ theme }) => theme.colors.textSecondary};
  font-style: italic;
  padding: ${({ theme }) => theme.spacing.md} 0;
`;

// ── Component ─────────────────────────────────────────────────────────────────

interface TeacherWorkloadProps {
  entries: TeacherWorkloadEntry[];
  loading?: boolean;
}

export const TeacherWorkload: React.FC<TeacherWorkloadProps> = ({ entries, loading }) => {
  if (loading) {
    return <div data-testid="candeza-reports-operations-workload-loading">Loading workload…</div>;
  }

  if (!entries.length) {
    return (
      <Empty data-testid="candeza-reports-operations-workload-empty">
        No session data this week.
      </Empty>
    );
  }

  return (
    <TableScroll data-testid="candeza-reports-operations-workload-scroll">
      <Table data-testid="candeza-reports-operations-workload-table">
        <thead>
          <tr>
            <th>Teacher</th>
            <th>Sessions</th>
            <th>Hours</th>
            <th>Utilization</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((t) => (
            <tr key={t.teacher_id} data-testid={`candeza-reports-operations-workload-row-${t.teacher_id}`}>
              <td>{t.teacher_name}</td>
              <td>{t.sessions_count}</td>
              <td>{t.hours_total.toFixed(1)}h</td>
              <td>{t.utilization_pct != null ? `${t.utilization_pct.toFixed(0)}%` : '—'}</td>
            </tr>
          ))}
        </tbody>
      </Table>
    </TableScroll>
  );
};

